import React, { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { Avatar, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { PersonOutline, LocalShippingOutlined, InsertDriveFile, InsertDriveFileOutlined } from '@mui/icons-material';
import { formatCurrency } from 'utils';
import { getAssetUrl } from 'utils/getAssetUrl';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { CONTACT_AVATAR_COLORS, DEAL_BUILDER_SUMMARY_ITEMS } from '../../constants';
import { getContactInitials } from '../../lib';
import { useDealBuilderStepNavigation, useDealBuilderSummary } from '../../hooks';
import { useSelectedContactsSummary } from '../../hooks/useSelectedContactsSummary';
import { useValidatedGoToStep } from '../../hooks/useValidateAndAdvance';
import { useDealBuilderContext } from '../../context/DealBuilderContext';
import { DealBuilderStep } from '../../types';
import { DealBuilderStepper } from './DealBuilderStepper';

export const BuildAQuoteCard = () => {
  const { viewStep, progressStep } = useDealBuilderStepNavigation();
  const validatedGoToStep = useValidatedGoToStep();
  const summary = useDealBuilderSummary();
  const { mainContact, additionalCount, avatarIndex } = useSelectedContactsSummary();
  const { selectedEquipmentItem, activeEntity, startNewQuote } = useDealBuilderContext();

  const completedSteps = useMemo(() => {
    const set = new Set<DealBuilderStep>();
    const hasContacts = !!mainContact;
    const hasEquipment = !!selectedEquipmentItem;
    if (hasContacts) set.add(DealBuilderStep.Contacts);
    if (hasContacts && hasEquipment) set.add(DealBuilderStep.Equipment);
    return set;
  }, [mainContact, selectedEquipmentItem]);

  return (
    <CardContainer
      title="Build a Quote"
      subTitle="Create a quote for this opportunity"
      style={{ height: 'auto', overflow: 'visible' }}
      actionBtn={
        activeEntity ? (
          <Button size="small" startIcon={<InsertDriveFileOutlined />} onClick={() => startNewQuote()}>
            Start New Quote
          </Button>
        ) : undefined
      }
    >
      <DealBuilderStepper
        viewStep={viewStep}
        progressStep={progressStep}
        completedSteps={completedSteps}
        onStepClick={validatedGoToStep}
      />

      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      >
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ flex: '0 0 60%', minWidth: 0 }}
        >
          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, flex: 1, minWidth: 0 }}>
            {mainContact ? (
              <>
                <Avatar
                  sx={{
                    bgcolor: CONTACT_AVATAR_COLORS[avatarIndex % CONTACT_AVATAR_COLORS.length],
                    color: 'black',
                  }}
                >
                  <Typography variant="subtitle2">{getContactInitials(mainContact.fullName)}</Typography>
                </Avatar>
                <Stack minWidth={0}>
                  <Typography variant="body2" noWrap>
                    {mainContact.fullName}
                  </Typography>
                  {mainContact.title && (
                    <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
                      {mainContact.title}
                    </Typography>
                  )}
                  {additionalCount > 0 && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      +{additionalCount} additional recipient{additionalCount > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>
              </>
            ) : (
              <>
                <Avatar>
                  <PersonOutline />
                </Avatar>
                <Typography variant="body2" color="text.secondary" noWrap>
                  No contact selected yet
                </Typography>
              </>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, flex: 1, minWidth: 0 }}>
            {selectedEquipmentItem ? (
              <>
                <Avatar
                  variant="rounded"
                  src={
                    selectedEquipmentItem.equipment.assets?.[0]?.source
                      ? getAssetUrl(selectedEquipmentItem.equipment.assets[0].source)
                      : undefined
                  }
                >
                  <LocalShippingOutlined />
                </Avatar>
                <Stack minWidth={0}>
                  <Typography variant="body2" noWrap>
                    {selectedEquipmentItem.equipment.details.name}
                  </Typography>
                </Stack>
              </>
            ) : (
              <>
                <Avatar variant="rounded">
                  <LocalShippingOutlined />
                </Avatar>
                <Typography variant="body2" color="text.secondary" noWrap>
                  No equipment selected yet
                </Typography>
              </>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ flex: '0 0 40%', minWidth: 0 }}
        >
          {DEAL_BUILDER_SUMMARY_ITEMS.map(({ key, label, color }) => (
            <Box
              key={key}
              sx={{
                px: 2,
                py: 1,
                flex: 1,
                minWidth: 0,
                bgcolor: theme => (color === 'default' ? 'transparent' : alpha(theme.palette[color].main, 0.08)),
              }}
            >
              <Typography
                variant="subtitle2"
                component="span"
                color={color === 'default' ? 'text.secondary' : `${color}.dark`}
                noWrap
                fontWeight={400}
              >
                {label}
              </Typography>
              <Typography variant="subtitle1" fontWeight={500} noWrap>
                {formatCurrency(summary[key], true)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </CardContainer>
  );
};
